import React, { useEffect, useMemo, useState } from 'react';
import { ApiError } from '../services/apiClient';
import { Client, CrmLead } from '../services/crmService';
import {
  CampaignPreviewResponse,
  CampaignRow,
  EMAIL_TEMPLATE_CATEGORIES,
  EmailAccountConnection,
  EmailTemplate,
  emailService,
} from '../services/emailService';
import { useToast } from '../contexts/ToastContext';
import { getReconnectErrorMeta } from '../utils/emailErrors';
import { OUTREACH_TEMPLATE_VARIABLES } from '../utils/emailTemplateVars';

interface OutreachCampaignModalProps {
  open: boolean;
  onClose: () => void;
  selectedClients: Client[];
  selectedLeads: CrmLead[];
  onSent?: (campaignId: string) => void;
}

const OutreachCampaignModal: React.FC<OutreachCampaignModalProps> = ({
  open,
  onClose,
  selectedClients,
  selectedLeads,
  onSent,
}) => {
  const [campaignName, setCampaignName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [accounts, setAccounts] = useState<EmailAccountConnection[]>([]);
  const [connectionId, setConnectionId] = useState('');
  const [preview, setPreview] = useState<CampaignPreviewResponse | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [reconnectRequired, setReconnectRequired] = useState(false);
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [existingCampaigns, setExistingCampaigns] = useState<CampaignRow[]>([]);
  const [existingCampaignId, setExistingCampaignId] = useState('');
  const { showToast } = useToast();

  useEffect(() => {
    if (!open) return;
    setTemplateId('');
    setPreview(null);
    setMode('new');
    void Promise.all([
      emailService.listAccounts(),
      emailService.listTemplates({ is_active: true }),
      emailService.listCampaigns().catch(() => [] as CampaignRow[]),
    ]).then(([ac, tm, cs]) => {
      setAccounts(ac);
      setTemplates(tm);
      setExistingCampaigns(cs);
      if (cs[0]) setExistingCampaignId(cs[0].id);
      const owned = ac.filter((a) => a.is_owned_by_current_user);
      const primary =
        owned.find((a) => a.is_primary) || owned[0] || ac.find((a) => a.is_primary) || ac[0];
      if (primary) setConnectionId(primary.id);
    });
  }, [open]);

  const handleTemplateChange = (tid: string) => {
    const hasDraft = subject.trim().length > 0 || body.trim().length > 0;
    if (tid && hasDraft) {
      const shouldReplace = window.confirm(
        'Apply this template and replace the current subject and body?'
      );
      if (!shouldReplace) return;
    }
    setTemplateId(tid);
    if (!tid) return;
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    setSubject(t.subject_template);
    setBody(t.body_template);
  };

  const totalSelected = selectedClients.length + selectedLeads.length;

  const requestPayload = useMemo(
    () => ({
      name: campaignName.trim(),
      subject: subject.trim(),
      body_template: body,
      client_ids: selectedClients.map((row) => row.id),
      lead_ids: selectedLeads.map((row) => row.id),
    }),
    [campaignName, subject, body, selectedClients, selectedLeads]
  );

  if (!open) return null;

  const canPreview = campaignName.trim() && subject.trim() && body.trim() && totalSelected > 0;

  const insertVariable = (variable: string) => {
    setBody((prev) => `${prev}${prev ? ' ' : ''}${variable}`);
  };

  const handlePreview = async () => {
    if (!canPreview) return;
    setLoadingPreview(true);
    try {
      const data = await emailService.previewCampaign(requestPayload);
      setPreview(data);
      showToast('Outreach preview updated.', 'info');
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'Preview failed.', 'error');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSend = async () => {
    setReconnectRequired(false);
    if (!canPreview) {
      showToast('Campaign name, subject, and body are required.', 'error');
      return;
    }
    const targetCount = preview?.will_send ?? totalSelected;
    const confirmed = window.confirm(`Send this outreach campaign to ${targetCount} leads?`);
    if (!confirmed) return;
    setSending(true);
    try {
      const campaign = await emailService.createCampaignDraft(requestPayload);
      const sentCampaign = await emailService.sendCampaign(campaign.id, connectionId || undefined);
      onSent?.(sentCampaign.id);
      showToast('Outreach campaign sent successfully.', 'success');
      onClose();
    } catch (err) {
      const reconnect = getReconnectErrorMeta(err);
      if (reconnect.requiresReconnect) {
        setReconnectRequired(true);
        showToast('Reconnect your mailbox in Settings → Email, then retry this campaign.', 'error');
      } else {
        showToast(err instanceof ApiError ? err.message : 'Campaign send failed.', 'error');
      }
    } finally {
      setSending(false);
    }
  };

  const handleAddToExisting = async () => {
    if (!existingCampaignId) {
      showToast('Pick a campaign to add these contacts to.', 'error');
      return;
    }
    const confirmed = window.confirm(`Add ${totalSelected} contact(s) to this campaign and send now?`);
    if (!confirmed) return;
    setSending(true);
    try {
      const res = await emailService.addCampaignRecipients(existingCampaignId, {
        client_ids: selectedClients.map((r) => r.id),
        lead_ids: selectedLeads.map((r) => r.id),
        connection_id: connectionId || undefined,
      });
      const s = res.add_summary;
      showToast(
        s ? `Added ${s.added} — ${s.sent} sent${s.skipped ? `, ${s.skipped} skipped` : ''}.` : 'Contacts added.',
        'success'
      );
      onSent?.(existingCampaignId);
      onClose();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'Could not add to campaign.', 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-3xl max-h-[92vh] overflow-auto rounded-2xl border border-border bg-surface p-6 space-y-4 shadow-xl">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-fg">Send Outreach</h3>
          <button type="button" onClick={onClose} className="text-sm text-fg-muted hover:text-fg">
            Close
          </button>
        </div>

        <p className="text-sm text-fg-muted">Selected recipients: {totalSelected}</p>

        <div className="inline-flex rounded-lg border border-border p-0.5 text-sm">
          <button
            type="button"
            onClick={() => setMode('new')}
            className={`rounded-md px-3 py-1 ${mode === 'new' ? 'bg-primary text-white' : 'text-fg-muted'}`}
          >
            New campaign
          </button>
          <button
            type="button"
            onClick={() => setMode('existing')}
            disabled={existingCampaigns.length === 0}
            className={`rounded-md px-3 py-1 disabled:opacity-40 ${
              mode === 'existing' ? 'bg-primary text-white' : 'text-fg-muted'
            }`}
          >
            Add to existing
          </button>
        </div>

        {mode === 'existing' ? (
          <div>
            <label className="block text-sm font-medium text-fg mb-1">Campaign</label>
            <select
              value={existingCampaignId}
              onChange={(e) => setExistingCampaignId(e.target.value)}
              className="w-full rounded-lg border border-border bg-input px-3 py-2 text-fg text-sm"
            >
              {existingCampaigns.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} — {c.total_recipients} recipients
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs text-fg-muted">
              These contacts get this campaign's existing message. Anyone already in it, on your
              do-not-contact list, or without an email is skipped.
            </p>
          </div>
        ) : null}

        {accounts.length === 0 ? (
          <p className="text-sm text-amber-700 dark:text-amber-300 rounded-lg bg-amber-50 dark:bg-amber-950/30 px-3 py-2">
            No email account is connected for your organization. Ask an admin or account owner to connect one.
          </p>
        ) : (
          <div>
            <label className="block text-sm font-medium text-fg mb-1">Send from</label>
            <select
              value={connectionId}
              onChange={(e) => setConnectionId(e.target.value)}
              className="w-full rounded-lg border border-border bg-input px-3 py-2 text-fg text-sm"
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.email_address} ({a.provider})
                  {a.connected_by_name ? ` — connected by ${a.connected_by_name}` : ''}
                  {a.allow_org_sending && !a.is_owned_by_current_user ? ' — shared with organization' : ''}
                </option>
              ))}
            </select>
          </div>
        )}
        {reconnectRequired ? (
          <p className="text-sm text-amber-700 dark:text-amber-300 rounded-lg bg-amber-50 dark:bg-amber-950/30 px-3 py-2">
            Campaign send is blocked until you reconnect your mailbox in{' '}
            <a href="/settings/email" className="underline font-medium">
              Settings → Email
            </a>
            .
          </p>
        ) : null}

        {mode === 'new' && (
        <>
        <div>
          <label className="block text-sm font-medium text-fg mb-1">Template</label>
          <select
            value={templateId}
            onChange={(e) => handleTemplateChange(e.target.value)}
            className="w-full rounded-lg border border-border bg-input px-3 py-2 text-fg text-sm"
          >
            <option value="">None</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} — {EMAIL_TEMPLATE_CATEGORIES.find((c) => c.value === t.category)?.label ?? t.category}
                {t.is_system_template ? ' (system)' : ''}
              </option>
            ))}
          </select>
          {templates.length === 0 ? (
            <p className="mt-2 text-xs text-fg-muted">
              No templates yet.{' '}
              <a href="/settings/email" className="underline font-medium">
                Create one in Settings → Email
              </a>
              .
            </p>
          ) : null}
        </div>

        <div className="grid gap-3">
          <input
            type="text"
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            placeholder="Campaign name"
            className="w-full rounded-lg border border-border bg-input px-3 py-2 text-fg text-sm"
          />
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject"
            className="w-full rounded-lg border border-border bg-input px-3 py-2 text-fg text-sm"
          />
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            placeholder="Write outreach email body"
            className="w-full rounded-lg border border-border bg-input px-3 py-2 text-fg text-sm"
          />
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium text-fg">Personalization variables</p>
          <div className="flex flex-wrap gap-2">
              {OUTREACH_TEMPLATE_VARIABLES.map((variable) => (
              <button
                key={variable}
                type="button"
                onClick={() => insertVariable(variable)}
                className="rounded-full border border-border px-3 py-1 text-xs text-fg-muted hover:text-fg hover:border-primary"
              >
                {variable}
              </button>
            ))}
          </div>
        </div>
        </>
        )}

        {mode === 'new' ? (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handlePreview}
              disabled={!canPreview || loadingPreview}
              className="px-3 py-2 rounded-xl border border-border text-sm text-fg disabled:opacity-50"
            >
              {loadingPreview ? 'Previewing…' : 'Preview'}
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={!canPreview || sending}
              className="px-3 py-2 rounded-xl bg-primary text-white text-sm disabled:opacity-50"
            >
              {sending ? 'Sending…' : 'Send Outreach'}
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleAddToExisting}
              disabled={sending || totalSelected === 0 || !existingCampaignId}
              className="px-3 py-2 rounded-xl bg-primary text-white text-sm disabled:opacity-50"
            >
              {sending ? 'Adding…' : 'Add to campaign'}
            </button>
          </div>
        )}

        {mode === 'new' && preview ? (
          <div className="rounded-lg border border-border bg-surface-muted/30 p-4 space-y-3">
            <h4 className="font-medium text-fg">Recipient preview</h4>
            <p className="text-sm text-fg-muted">
              Total selected: {preview.total_selected} | Will send: {preview.will_send} | Skipped: {preview.skipped}
            </p>
            <div className="text-sm">
              {Object.keys(preview.skipped_reasons).length === 0 ? (
                <p className="text-fg-muted">No skipped recipients in preview.</p>
              ) : (
                Object.entries(preview.skipped_reasons).map(([reason, count]) => (
                  <p key={reason} className="text-fg-muted">
                    {reason}: {count}
                  </p>
                ))
              )}
            </div>
            {preview.sample_personalized_email ? (
              <div className="rounded-lg border border-border bg-surface p-3 space-y-2">
                <p className="text-xs text-fg-muted">Sample personalized email ({preview.sample_personalized_email.email})</p>
                <p className="text-sm font-medium text-fg">{preview.sample_personalized_email.subject}</p>
                <pre className="whitespace-pre-wrap text-xs text-fg-muted">{preview.sample_personalized_email.body}</pre>
              </div>
            ) : (
              <p className="text-sm text-fg-muted">No sendable recipients after suppression checks.</p>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default OutreachCampaignModal;
